import React, { createContext, useState, useContext, useEffect, useCallback } from "react";
import axios from "axios";
import { API_URL } from "../config";
import posthog from "posthog-js";
import { identifyUserInPostHog } from "../utils/posthog";
import { STORAGE_KEYS } from "../constants/storageKeys";

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem("user");
    return storedUser ? JSON.parse(storedUser) : null;
  });
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const accessToken = localStorage.getItem("access_token");

      if (!accessToken) {
        clearUserData();
        setLoading(false);
        return;
      }
      try {
        const response = await axios.get(`${API_URL}/auth/me`, {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        });

        if (response.data) {
          const userData = {
            email: response.data.email,
            name: response.data.name,
            picture: response.data.picture,
            subscription_type: response.data.subscription_type || 0,
            subscription_end_date: response.data.subscription_end_date,
            account_type: response.data.account_type,
            has_had_trial: response.data.has_had_trial || false,
            dot_authorized: response.data.dot_authorized,
          };

          if (userData.picture) {
            try {
              const img = new Image();
              img.onload = () => {};
              img.onerror = () => {
                userData.picture =
                  "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
              };
              img.src = userData.picture;
            } catch (error) {
              userData.picture =
                "https://www.gravatar.com/avatar/00000000000000000000000000000000?d=mp&f=y";
            }
          }

          // Set app state + localStorage
          setUser(userData);
          localStorage.setItem("user", JSON.stringify(userData));

          // ----------------------------
          // PostHog identify block:
          // ----------------------------
          // Identify the user in PostHog so recordings/events attach to this person.
          identifyUserInPostHog(response.data?.email, userData);
          // End of PostHog identify block:
          // ----------------------------
        }
      } catch (error) {
        if (error.response?.status === 401) {
          clearUserData();
        }
        console.error("Error verifying auth:", error);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const refreshUser = async () => {
    const token = localStorage.getItem("access_token");
    if (!token) return;
    const res = await axios.get(`${API_URL}/auth/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    setUser(res.data);

    // Optionally re-identify/update properties in PostHog when profile refreshes
    identifyUserInPostHog(res.data?.email, res.data);
  };

  const clearUserData = () => {
    setUser(null);
    localStorage.removeItem("user");
    localStorage.removeItem("access_token");
  };

  const logout = async () => {
    try {
      const token = localStorage.getItem("access_token");
      if (token) {
        try {
          await axios.post(`${API_URL}/auth/logout`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          });
        } catch (error) {
          console.error("Backend logout error:", error);
        }
      }
    } catch (error) {
      console.error("Logout error:", error);
    } finally {
      try {
        if (window.posthog) {
          posthog.reset();
          // Clear the last identified user tracking
          localStorage.removeItem(STORAGE_KEYS.POSTHOG_LAST_IDENTIFIED_USER);
        }
      } catch (err) {
        // ignore errors from posthog reset
      }

      clearUserData();
    }
  };

  const updateUser = (userData) => {
    setUser(userData);
    if (userData) {
      localStorage.setItem("user", JSON.stringify(userData));

      // --- PostHog identify: ensure the browser session is mapped to this user
      // This guarantees client events + recordings show the same person as server events.
      identifyUserInPostHog(userData?.email, userData);
      // --- identify block end
      
    } else {
      clearUserData();
    }
  };

  useEffect(() => {
    const interceptor = axios.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem("access_token");
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.request.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        if (error.response?.status === 401) {
          clearUserData();
        }
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, []);

  useEffect(() => {
    const controller = new AbortController();

    const bootstrapAuth = async () => {
      const token = localStorage.getItem("access_token");
      if (!token) {
        clearUserData();
        setLoading(false);
        return;
      }

      try {
        const res = await axios.get(`${API_URL}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          signal: controller.signal,
        });
        const profile = res.data;
        const userData = {
          email: profile.email,
          name: profile.name,
          picture: profile.picture,
          subscription_type: profile.subscription_type || 0,
          subscription_end_date: profile.subscription_end_date,
          account_type: profile.account_type,
          has_had_trial: profile.has_had_trial || false,
          dot_authorized: profile.dot_authorized,
        };
        setUser(userData);
        localStorage.setItem("user", JSON.stringify(userData));

        if (process.env.REACT_APP_POSTHOG_KEY && window.posthog && userData.email) {
          posthog.identify(userData.email);
          posthog.setPersonProperties({
            email: userData.email,
            name: userData.name,
          });
        }
      } catch (error) {
        if (!axios.isCancel(error)) {
          clearUserData();
        }
      } finally {
        setLoading(false);
      }
    };

    bootstrapAuth();

    return () => {
      controller.abort();
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        refreshUser,
        logout,
        updateUser,
        token: localStorage.getItem("access_token"),
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
